import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import {
  LayoutDashboard, Users, ImageIcon, CreditCard, Repeat, Receipt, Tag, Package, Layers,
  Server, Cpu, Wrench, LineChart as LineIcon, Settings as SettingsIcon, ScrollText, Zap, Shield,
  Check, X, Loader2, Search, Plus, Minus, Trash2, RefreshCw as RefreshIcon,
} from "lucide-react";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "admins", label: "Admins", icon: Shield },
  { id: "generations", label: "Generations", icon: ImageIcon },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "subscriptions", label: "Subscriptions", icon: Repeat },
  { id: "transactions", label: "Credit Ledger", icon: Receipt },
  { id: "pricing", label: "Pricing", icon: Tag },
  { id: "packages", label: "Credit Packages", icon: Package },
  { id: "plans", label: "Plans", icon: Layers },
  { id: "providers", label: "AI Providers", icon: Server },
  { id: "models", label: "Models", icon: Cpu },
  { id: "tools", label: "Tools", icon: Wrench },
  { id: "analytics", label: "Analytics", icon: LineIcon },
  { id: "home_tiles", label: "Home Tiles", icon: LayoutDashboard },
  { id: "gallery_mgr", label: "Gallery", icon: ImageIcon },
  { id: "login_video", label: "Login Video", icon: ImageIcon },
  { id: "legal_pages", label: "Legal Pages", icon: ScrollText },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "audit", label: "Audit Logs", icon: ScrollText },
];

const COLORS = ["#00CFFF", "#19F5D2", "#00E5FF", "#71859A", "#A9BDD0", "#10b981", "#8b5cf6"];

const Stat = ({ label, value, sub }) => (
  <div className="studio-card p-5">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-2 text-2xl font-bold">{value}</p>
    {sub != null && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

function Table({ columns, rows, empty = "No data" }) {
  if (!rows?.length) return <p className="p-8 text-center text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase text-muted-foreground">
          <tr>{columns.map((c) => <th key={c.key} className="whitespace-nowrap p-3">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/60 hover:bg-secondary/20">
              {columns.map((c) => <td key={c.key} className="whitespace-nowrap p-3">{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="flex min-h-screen bg-secondary/20">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <img src="/adgen-logo-v2.png" alt="AdGen Store" className="h-8 w-8 rounded-md object-contain" draggable={false} />
          <span className="font-display font-extrabold">Admin</span>
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
              className={`mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === n.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => navigate("/dashboard")} className="border-t border-border px-5 py-3 text-left text-sm text-muted-foreground hover:text-foreground">← Back to app</button>
      </aside>

      <div className="flex-1 overflow-hidden">
        <div className="border-b border-border bg-card px-5 py-3 md:hidden">
          <select value={tab} onChange={(e) => setTab(e.target.value)} className="w-full rounded-lg border border-border bg-background p-2 text-sm">
            {NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        </div>
        <div className="p-5 sm:p-8">
          <Section tab={tab} />
        </div>
      </div>
    </div>
  );
}

function Section({ tab }) {
  switch (tab) {
    case "dashboard": return <DashboardSection />;
    case "users": return <UsersSection />;
    case "admins": return <AdminsSection />;
    case "generations": return <GenerationsSection />;
    case "payments": return <ListSection title="Payments" url="/admin/payments" columns={[
      { key: "id", label: "Order", render: (r) => <span className="font-mono text-xs">{r.id.slice(0, 12)}</span> },
      { key: "gateway", label: "Gateway" }, { key: "amount", label: "Amount", render: (r) => `₹${r.amount}` },
      { key: "credits", label: "Credits" }, { key: "status", label: "Status", render: (r) => <StatusBadge s={r.status} /> },
      { key: "created_at", label: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
    ]} />;
    case "subscriptions": return <ListSection title="Subscriptions" url="/admin/subscriptions" columns={[
      { key: "plan", label: "Plan", render: (r) => <span className="capitalize">{r.plan}</span> }, { key: "provider", label: "Provider" },
      { key: "amount", label: "Amount", render: (r) => `₹${r.amount}` }, { key: "credits", label: "Credits" },
      { key: "status", label: "Status", render: (r) => <StatusBadge s={r.status} /> },
      { key: "start_date", label: "Start", render: (r) => new Date(r.start_date).toLocaleDateString() },
    ]} />;
    case "transactions": return <ListSection title="Credit Ledger" url="/admin/transactions" columns={[
      { key: "type", label: "Type", render: (r) => <Badge variant="secondary">{r.type}</Badge> },
      { key: "amount", label: "Amount", render: (r) => <span className={r.amount >= 0 ? "text-emerald-600" : "text-destructive"}>{r.amount >= 0 ? "+" : ""}{r.amount}</span> },
      { key: "balance_before", label: "Before" }, { key: "balance_after", label: "After" },
      { key: "description", label: "Description" }, { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleString() },
    ]} />;
    case "pricing": return <PricingSection />;
    case "packages": return <PackagesSection />;
    case "plans": return <PlansSection />;
    case "providers": return <ProvidersSection />;
    case "models": return <ModelsSection />;
    case "tools": return <ToolsSection />;
    case "analytics": return <AnalyticsSection />;
    case "home_tiles": return <HomeTilesSection />;
    case "gallery_mgr": return <GalleryManagerSection />;
    case "login_video": return <LoginVideoSection />;
    case "legal_pages": return <LegalPagesSection />;
    case "settings": return <SettingsSection />;
    case "audit": return <ListSection title="Audit Logs" url="/admin/audit-logs" columns={[
      { key: "admin_email", label: "Admin" }, { key: "action", label: "Action" }, { key: "target", label: "Target" },
      { key: "new_value", label: "New value", render: (r) => JSON.stringify(r.new_value) },
      { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleString() },
    ]} />;
    default: return null;
  }
}

const StatusBadge = ({ s }) => {
  const map = { completed: "bg-emerald-500/15 text-emerald-600", paid: "bg-emerald-500/15 text-emerald-600", active: "bg-emerald-500/15 text-emerald-600", failed: "bg-destructive/15 text-destructive", processing: "bg-amber-500/15 text-amber-600", created: "bg-secondary text-muted-foreground", cancelled: "bg-secondary text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${map[s] || "bg-secondary text-muted-foreground"}`}>{s}</span>;
};

function DashboardSection() {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const pie = [{ name: "Basic", value: d.basic_usage }, { name: "Premium", value: d.premium_usage }];
  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Business Overview</h1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total Users" value={d.total_users} sub={`${d.active_users} active`} />
        <Stat label="Generations" value={d.total_generations} sub={`${d.failed_generations} failed`} />
        <Stat label="Revenue" value={`₹${d.revenue}`} sub={`${d.credits_sold} credits sold`} />
        <Stat label="Gross Profit" value={`₹${d.gross_profit}`} sub={`AI cost $${d.provider_cost}`} />
        <Stat label="Credits Consumed" value={d.credits_consumed} />
        <Stat label="Payment Success" value={`${d.payment_success_rate}%`} />
        <Stat label="Premium Usage" value={d.premium_usage} />
        <Stat label="Basic Usage" value={d.basic_usage} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="studio-card p-6">
          <p className="mb-4 font-semibold">Basic vs Premium</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>{pie.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="studio-card p-6">
          <p className="mb-4 font-semibold">Model uptime</p>
          <div className="grid h-[220px] place-items-center">
            <div className="text-center"><p className="text-5xl font-extrabold text-primary">99.9%</p><p className="mt-2 text-sm text-muted-foreground">All providers healthy</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsSection() {
  const [a, setA] = useState(null);
  useEffect(() => { client.get("/admin/analytics").then(({ data }) => setA(data)); }, []);
  if (!a) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Analytics</h1>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="studio-card p-6">
          <p className="mb-4 font-semibold">Tool popularity</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={a.tool_popularity}><XAxis dataKey="service" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill="#00CFFF" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="studio-card p-6">
          <p className="mb-4 font-semibold">Generations over time</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={a.generations_over_time}><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill="#19F5D2" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function ListSection({ title, url, columns }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { client.get(url + "?limit=50").then(({ data }) => setRows(data.items || data)).finally(() => setLoading(false)); }, [url]);
  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">{title}</h1>
      <div className="studio-card">{loading ? <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : <Table columns={columns} rows={rows} />}</div>
    </div>
  );
}

function UsersSection() {
  const { user: currentAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState(50);

  const load = useCallback(() => { client.get(`/admin/users?q=${encodeURIComponent(q)}&limit=50`).then(({ data }) => setRows(data.items)); }, [q]);
  useEffect(() => { load(); }, [load]);

  const adjust = async (uid, amt) => {
    try { const { data } = await client.post(`/admin/users/${uid}/credits`, { amount: amt, reason: "Admin adjustment" }); toast.success(`New balance: ${data.balance}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleStatus = async (u) => {
    const next = u.status === "disabled" ? "active" : "disabled";
    try { await client.post(`/admin/users/${u.user_id}/status`, { status: next }); toast.success(`User ${next}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const removeUser = async (u) => {
    if (currentAdmin && u.user_id === currentAdmin.user_id) {
      return toast.error("You cannot delete your own admin account");
    }
    const ok = window.confirm(`Permanently delete ${u.email}?\n\nThis removes their account and all associated data (wallet, credit ledger, generations, payments, subscriptions, sessions). This cannot be undone.`);
    if (!ok) return;
    try { await client.delete(`/admin/users/${u.user_id}`); toast.success(`Deleted ${u.email}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Users</h1>
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="admin-user-search" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
      </div>
      <div className="studio-card">
        <Table rows={rows} columns={[
          { key: "name", label: "Name" }, { key: "email", label: "Email" },
          { key: "role", label: "Role", render: (r) => <Badge variant={r.role === "admin" ? "default" : "secondary"}>{r.role}</Badge> },
          { key: "plan", label: "Plan", render: (r) => <span className="capitalize">{r.plan}</span> },
          { key: "credits", label: "Credits", render: (r) => <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-primary" fill="currentColor" />{r.credits}</span> },
          { key: "status", label: "Status", render: (r) => <StatusBadge s={r.status} /> },
          { key: "actions", label: "Actions", render: (r) => (
            <div className="flex gap-1">
              <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`add-credits-${r.user_id}`} onClick={() => adjust(r.user_id, 50)}><Plus className="h-4 w-4" /></Button>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => adjust(r.user_id, -50)}><Minus className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => toggleStatus(r)}>{r.status === "disabled" ? "Enable" : "Disable"}</Button>
              <Button
                size="icon" variant="outline"
                className="h-8 w-8 border-destructive/40 text-destructive hover:bg-destructive/10"
                title={currentAdmin && r.user_id === currentAdmin.user_id ? "You cannot delete your own account" : "Delete user"}
                disabled={currentAdmin && r.user_id === currentAdmin.user_id}
                data-testid={`delete-user-${r.user_id}`}
                onClick={() => removeUser(r)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ) },
        ]} />
      </div>
    </div>
  );
}

function PricingSection() {
  const [rows, setRows] = useState([]);
  const load = () => client.get("/admin/pricing").then(({ data }) => setRows(data.sort((a, b) => a.service.localeCompare(b.service))));
  useEffect(() => { load(); }, []);
  const save = async (r, credits) => {
    try { await client.put("/admin/pricing", { service: r.service, model: r.model, credits: Number(credits), enabled: r.enabled }); toast.success("Pricing updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Pricing Rules</h1>
      <div className="studio-card">
        <Table rows={rows} columns={[
          { key: "service", label: "Service" },
          { key: "model", label: "Model", render: (r) => <span className="capitalize">{r.model}</span> },
          { key: "credits", label: "Credits", render: (r) => (
            <Input type="number" defaultValue={r.credits} className="h-8 w-20" data-testid={`price-${r.service}-${r.model}`} onBlur={(e) => save(r, e.target.value)} />
          ) },
          { key: "enabled", label: "Enabled", render: (r) => <Switch checked={r.enabled} onCheckedChange={(v) => { client.put("/admin/pricing", { service: r.service, model: r.model, credits: r.credits, enabled: v }).then(load); }} /> },
        ]} />
      </div>
    </div>
  );
}

function PackagesSection() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => client.get("/admin/packages").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await client.post("/admin/packages", edit); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <div className="mb-6 flex items-center justify-between"><h1 className="font-display text-2xl font-extrabold">Credit Packages</h1>
        <Button className="rounded-full" data-testid="new-package" onClick={() => setEdit({ name: "", credits: 100, price: 100, currency: "INR", status: "active", featured: false, display_order: 0 })}><Plus className="mr-1 h-4 w-4" /> New</Button>
      </div>
      <div className="studio-card">
        <Table rows={rows} columns={[
          { key: "name", label: "Name" }, { key: "credits", label: "Credits" }, { key: "price", label: "Price", render: (r) => `₹${r.price}` },
          { key: "status", label: "Status", render: (r) => <StatusBadge s={r.status} /> },
          { key: "featured", label: "Featured", render: (r) => r.featured ? <Check className="h-4 w-4 text-primary" /> : "—" },
          { key: "actions", label: "", render: (r) => <Button size="sm" variant="outline" onClick={() => setEdit(r)}>Edit</Button> },
        ]} />
      </div>
      <EditDialog open={!!edit} onClose={() => setEdit(null)} title="Credit Package" onSave={save} fields={edit && [
        { key: "name", label: "Name" }, { key: "credits", label: "Credits", type: "number" }, { key: "price", label: "Price (₹)", type: "number" },
        { key: "display_order", label: "Order", type: "number" }, { key: "featured", label: "Featured", type: "switch" },
      ]} value={edit} setValue={setEdit} />
    </div>
  );
}

function PlansSection() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => client.get("/admin/plans").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await client.post("/admin/plans", { ...edit, features: typeof edit.features === "string" ? edit.features.split("\n").filter(Boolean) : edit.features }); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <div className="mb-6 flex items-center justify-between"><h1 className="font-display text-2xl font-extrabold">Subscription Plans</h1>
        <Button className="rounded-full" onClick={() => setEdit({ name: "", price: 0, credits: 0, interval: "month", features: "", premium_access: true, priority_processing: false, enabled: true, display_order: 0 })}><Plus className="mr-1 h-4 w-4" /> New</Button>
      </div>
      <div className="studio-card">
        <Table rows={rows} columns={[
          { key: "name", label: "Name" }, { key: "price", label: "Price", render: (r) => `₹${r.price}/mo` }, { key: "credits", label: "Credits" },
          { key: "razorpay_plan_id", label: "Razorpay Plan ID", render: (r) => r.razorpay_plan_id ? <span className="font-mono text-xs">{r.razorpay_plan_id}</span> : <span className="text-muted-foreground text-xs">— not set —</span> },
          { key: "premium_access", label: "Premium", render: (r) => r.premium_access ? <Check className="h-4 w-4 text-primary" /> : "—" },
          { key: "enabled", label: "Enabled", render: (r) => r.enabled ? <Check className="h-4 w-4 text-primary" /> : "—" },
          { key: "actions", label: "", render: (r) => <Button size="sm" variant="outline" onClick={() => setEdit({ ...r, features: r.features.join("\n") })}>Edit</Button> },
        ]} />
      </div>
      <EditDialog open={!!edit} onClose={() => setEdit(null)} title="Subscription Plan" onSave={save} fields={edit && [
        { key: "name", label: "Name" }, { key: "price", label: "Price (₹)", type: "number" }, { key: "credits", label: "Credits", type: "number" },
        { key: "features", label: "Features (one per line)", type: "textarea" },
        { key: "razorpay_plan_id", label: "Razorpay Plan ID (autopay)", placeholder: "plan_XXXXXXXXXXXX" },
        { key: "premium_access", label: "Premium access", type: "switch" },
        { key: "priority_processing", label: "Priority", type: "switch" }, { key: "enabled", label: "Enabled", type: "switch" },
      ]} value={edit} setValue={setEdit} />
    </div>
  );
}

/* --- AI Providers -------------------------------------------------------- */

// Small "5m ago" pretty-printer for the balance freshness label.
function shortAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 45) return "just now";
  if (s < 90) return "1m ago";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 5400) return "1h ago";
  return `${Math.floor(s / 3600)}h ago`;
}

function BalanceCell({ provider, balance, onRefresh }) {
  const b = balance || {};
  const loading = b._loading && !b.status;
  const dim = !provider.enabled ? "opacity-60" : "";
  let content;
  if (loading) {
    content = <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> checking…</span>;
  } else if (b.status === "ok") {
    content = <span className={`font-semibold ${b.low ? "text-amber-500" : "text-primary"}`} title={b.raw_value != null ? String(b.raw_value) : ""} data-testid={`balance-value-${provider.id}`}>{b.display}</span>;
  } else if (b.status === "error") {
    content = <span className="text-xs text-destructive" title={b.error || "Fetch failed"} data-testid={`balance-error-${provider.id}`}>Error</span>;
  } else if (b.status === "not_available") {
    content = <span className="text-xs text-muted-foreground" data-testid={`balance-na-${provider.id}`}>Not available</span>;
  } else {
    content = <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className={`flex flex-col gap-0.5 ${dim}`} data-testid={`balance-cell-${provider.id}`}>
      <div className="flex items-center gap-2">
        {content}
        <button className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                onClick={onRefresh}
                title="Refresh balance"
                data-testid={`balance-refresh-${provider.id}`}
                disabled={b._loading}>
          <RefreshIcon className={`h-3 w-3 ${b._loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {b.fetched_at && (b.status === "ok" || b.status === "error") && (
        <span className="text-[10px] text-muted-foreground">last updated {shortAgo(b.fetched_at)}</span>
      )}
    </div>
  );
}

function ProvidersSection() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [balances, setBalances] = useState({}); // { [providerId]: {status, display, ...} }
  const [refreshingAll, setRefreshingAll] = useState(false);
  const load = () => client.get("/admin/providers").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  // Fetch balance for a single provider. `force` bypasses the 5-min server cache.
  const fetchBalance = React.useCallback(async (pid, force = false) => {
    setBalances((b) => ({ ...b, [pid]: { ...(b[pid] || {}), _loading: true } }));
    try {
      const { data } = await client.get(`/admin/providers/${pid}/balance${force ? "?force=1" : ""}`);
      setBalances((b) => ({ ...b, [pid]: { ...data, _loading: false } }));
    } catch (e) {
      setBalances((b) => ({ ...b, [pid]: { status: "error", error: apiErr(e), _loading: false } }));
    }
  }, []);

  // Poll balances for every provider row on a 5-minute interval. Cleared on
  // unmount so no background polling happens on other admin pages.
  useEffect(() => {
    if (!rows.length) return;
    const pollAll = () => rows.forEach((r) => fetchBalance(r.id));
    pollAll();
    const id = setInterval(pollAll, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [rows, fetchBalance]);

  const refreshAll = async () => {
    setRefreshingAll(true);
    await Promise.all(rows.map((r) => fetchBalance(r.id, true)));
    setRefreshingAll(false);
    toast.success("Balances refreshed");
  };

  const test = async (p) => {
    try { const { data } = await client.post(`/admin/providers/${p.id}/test`); data.connected ? toast.success(data.message) : toast.error(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (p, v) => { try { await client.put("/admin/providers", { id: p.id, enabled: v }); toast.success(v ? "Enabled" : "Disabled"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (p) => { try { await client.delete(`/admin/providers/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const save = async () => {
    const models = (edit.modelsText || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [id, ...rest] = l.split("|"); return { id: id.trim(), label: (rest.join("|") || id).trim() };
    });
    const payload = {
      id: edit.id || undefined, name: edit.name, type: edit.type, base_url: edit.base_url,
      default_model: edit.default_model, priority: edit.priority, enabled: edit.enabled, models,
      async_polling: !!edit.async_polling,
      poll_path: edit.poll_path || null,
      poll_done_field: edit.poll_done_field || null,
      poll_done_value: edit.poll_done_value || null,
      result_url_field: edit.result_url_field || null,
      headers_json: edit.headers_json || null,
      auth_header: edit.auth_header || null,
      auth_prefix: edit.auth_prefix === undefined ? null : edit.auth_prefix,
    };
    if (edit.api_key && !edit.api_key.startsWith("••")) payload.api_key = edit.api_key;
    try {
      if (edit.id) await client.put("/admin/providers", payload);
      else await client.post("/admin/providers", payload);
      toast.success("Provider saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openEdit = (p) => setEdit({ ...p, api_key: "", modelsText: (p.models || []).map((m) => `${m.id}|${m.label || m.id}`).join("\n") });
  const openNew = () => setEdit({ name: "", type: "custom", base_url: "", api_key: "", default_model: "", priority: "fallback", enabled: false, modelsText: "", async_polling: false, auth_header: "Authorization", auth_prefix: "Bearer " });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">AI Providers</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" onClick={refreshAll} disabled={refreshingAll} data-testid="refresh-all-balances">
            {refreshingAll ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshIcon className="mr-1 h-4 w-4" />} Refresh All
          </Button>
          <Button className="rounded-full" data-testid="new-provider" onClick={openNew}><Plus className="mr-1 h-4 w-4" /> Add Provider</Button>
        </div>
      </div>
      <div className="studio-card">
        <Table rows={rows} columns={[
          { key: "name", label: "Provider" },
          { key: "type", label: "Type", render: (r) => <Badge variant="secondary">{r.type}</Badge> },
          { key: "models", label: "Models", render: (r) => <span className="text-xs">{(r.models || []).length} model(s)</span> },
          { key: "status", label: "Health", render: (r) => <StatusBadge s={r.status} /> },
          { key: "balance", label: "Credits / Balance", render: (r) => (
            <BalanceCell provider={r} balance={balances[r.id]} onRefresh={() => fetchBalance(r.id, true)} />
          ) },
          { key: "has_key", label: "Key", render: (r) => r.has_key ? <Check className="h-4 w-4 text-primary" /> : (r.type === "gemini" ? <span className="text-xs text-muted-foreground">Emergent</span> : <X className="h-4 w-4 text-muted-foreground" />) },
          { key: "priority", label: "Priority", render: (r) => <span className="capitalize">{r.priority}</span> },
          { key: "enabled", label: "On", render: (r) => <Switch checked={r.enabled} data-testid={`provider-toggle-${r.id}`} onCheckedChange={(v) => toggle(r, v)} /> },
          { key: "actions", label: "Actions", render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" data-testid={`test-provider-${r.id}`} onClick={() => test(r)}>Test</Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(r)}>Configure</Button>
              {r.type !== "mock" && <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]} />
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Configure Provider" : "Add AI Provider"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div><Label>Name</Label><Input className="mt-1.5" data-testid="provider-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div><Label>Type</Label>
                <select className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm" data-testid="provider-type" value={edit.type || ""} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>
                  <option value="custom">Custom / OpenAI-compatible</option>
                  <option value="gemini">Google Nano Banana (gemini)</option>
                  <option value="google_veo">Google Veo (video, async)</option>
                  <option value="xai">xAI Grok Imagine (xai)</option>
                  <option value="xai_video">xAI Grok Imagine Video</option>
                  <option value="flux">FLUX / BFL (flux)</option>
                  <option value="openai_image">OpenAI-compatible images</option>
                </select>
                {edit.type === "custom" && <p className="mt-1 text-xs text-muted-foreground">Generic adapter — configure base URL, auth header, and optional async polling below.</p>}
              </div>
              <div><Label>Base URL</Label><Input className="mt-1.5 font-mono text-sm" placeholder="https://api.x.ai/v1" value={edit.base_url || ""} onChange={(e) => setEdit({ ...edit, base_url: e.target.value })} /></div>
              <div><Label>API Key {edit.type === "gemini" && <span className="text-xs text-muted-foreground">(optional — uses Emergent key)</span>}</Label><Input type="password" className="mt-1.5 font-mono text-sm" data-testid="provider-api-key" placeholder={edit.has_key ? "Enter to replace" : "Paste API key"} value={edit.api_key || ""} onChange={(e) => setEdit({ ...edit, api_key: e.target.value })} /></div>

              {edit.type === "custom" && (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Auth header</Label>
                      <Input className="mt-1.5 font-mono text-sm" data-testid="provider-auth-header" placeholder="Authorization"
                             value={edit.auth_header || ""} onChange={(e) => setEdit({ ...edit, auth_header: e.target.value })} />
                    </div>
                    <div>
                      <Label>Auth prefix</Label>
                      <Input className="mt-1.5 font-mono text-sm" placeholder="Bearer "
                             value={edit.auth_prefix === undefined ? "Bearer " : edit.auth_prefix} onChange={(e) => setEdit({ ...edit, auth_prefix: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Extra headers (JSON, optional)</Label>
                    <textarea rows={2} className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 font-mono text-xs"
                              placeholder='{"x-custom-header": "value"}'
                              value={edit.headers_json || ""} onChange={(e) => setEdit({ ...edit, headers_json: e.target.value })} />
                  </div>
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">Async polling</span>
                        <p className="text-xs text-muted-foreground">Submit → poll → retrieve. For Veo-style providers.</p>
                      </div>
                      <Switch checked={!!edit.async_polling} data-testid="provider-async"
                              onCheckedChange={(v) => setEdit({ ...edit, async_polling: v })} />
                    </div>
                    {edit.async_polling && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div><Label className="text-xs">Poll path</Label><Input className="mt-1.5 font-mono text-xs" placeholder="/videos/{job_id}"
                                value={edit.poll_path || ""} onChange={(e) => setEdit({ ...edit, poll_path: e.target.value })} /></div>
                        <div><Label className="text-xs">"Done" field (dotted)</Label><Input className="mt-1.5 font-mono text-xs" placeholder="status"
                                value={edit.poll_done_field || ""} onChange={(e) => setEdit({ ...edit, poll_done_field: e.target.value })} /></div>
                        <div><Label className="text-xs">"Done" value</Label><Input className="mt-1.5 font-mono text-xs" placeholder="done"
                                value={edit.poll_done_value || ""} onChange={(e) => setEdit({ ...edit, poll_done_value: e.target.value })} /></div>
                        <div><Label className="text-xs">Result URL field (dotted)</Label><Input className="mt-1.5 font-mono text-xs" placeholder="video.url"
                                value={edit.result_url_field || ""} onChange={(e) => setEdit({ ...edit, result_url_field: e.target.value })} /></div>
                      </div>
                    )}
                  </div>
                </>
              )}
              <div><Label>Models <span className="text-xs text-muted-foreground">(one per line: <code>model-id|Label</code>)</span></Label>
                <textarea rows={4} className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 font-mono text-xs" data-testid="provider-models" value={edit.modelsText || ""} onChange={(e) => setEdit({ ...edit, modelsText: e.target.value })} placeholder={"grok-imagine-image|Grok Imagine\ngrok-imagine-image-2.0|Grok Imagine 2.0"} />
              </div>
              <div><Label>Default model ID</Label><Input className="mt-1.5 font-mono text-sm" data-testid="provider-default-model" value={edit.default_model || ""} onChange={(e) => setEdit({ ...edit, default_model: e.target.value })} /></div>
              <div><Label>Priority</Label>
                <select className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm" value={edit.priority || "fallback"} onChange={(e) => setEdit({ ...edit, priority: e.target.value })}>
                  <option value="primary">Primary</option>
                  <option value="fallback">Fallback</option>
                </select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <span className="font-medium">Enabled</span>
                <Switch checked={!!edit.enabled} data-testid="provider-enabled" onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="provider-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModelsSection() {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => { client.get("/admin/models").then(({ data }) => setRows(data)); client.get("/admin/providers").then(({ data }) => setProviders(data)); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await client.put("/admin/models", { customer_model: edit.customer_model, provider_id: edit.provider_id, provider_model_id: edit.provider_model_id, enabled: edit.enabled, est_cost: Number(edit.est_cost) }); toast.success("Saved"); setEdit(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const quickToggle = async (m, v) => {
    try {
      await client.put("/admin/models", { customer_model: m.customer_model, enabled: v });
      toast.success(`${m.label} ${v ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Customer Models</h1>
          <p className="text-sm text-muted-foreground">Map customer-facing models to providers. Toggle off to hide from users.</p>
        </div>
        <Button data-testid="add-model-button" className="gap-1.5"
                onClick={() => setEdit({ _new: true, customer_model: "", label: "", tagline: "",
                                          media_type: "image", tier: "basic", enabled: true,
                                          est_cost: 0, services: [], provider_id: "",
                                          provider_model_id: "",
                                          initial_prompt: "", prompt_enhancement_enabled: false })}>
          <Plus className="h-4 w-4" /> Add New Model
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {rows.map((m) => (
          <div key={m.id} className="studio-card p-6">
            <div className="flex items-center justify-between gap-3">
              <p className="font-bold capitalize">{m.label} {m.recommended && <Badge className="ml-2">Recommended</Badge>}</p>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${m.enabled ? "text-emerald-600" : "text-muted-foreground"}`}>{m.enabled ? "Live" : "Hidden"}</span>
                <Switch checked={!!m.enabled} data-testid={`model-toggle-${m.customer_model}`} onCheckedChange={(v) => quickToggle(m, v)} />
              </div>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{m.tagline}</p>
            <dl className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Provider model</dt><dd className="font-mono">{m.provider_model_id}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Est. cost</dt><dd>${m.est_cost}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Media type</dt><dd className="capitalize">{m.media_type || "image"}</dd></div>
            </dl>
            <Button size="sm" variant="outline" className="mt-4" data-testid={`config-model-${m.customer_model}`} onClick={() => setEdit({ ...m, initial_prompt: m.initial_prompt || "", prompt_enhancement_enabled: !!m.prompt_enhancement_enabled })}>Configure</Button>
          </div>
        ))}
      </div>
      <ModelConfigDialog edit={edit} setEdit={setEdit} providers={providers} onSaved={load} />
    </div>
  );
}

function ModelConfigDialog({ edit, setEdit, providers, onSaved }) {
  const [testPrompt, setTestPrompt] = useState("create an image of naruto anime");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tools, setTools] = useState([]);

  useEffect(() => {
    setPreview(null);
    // Load the tool catalog so admins can toggle per-tool availability.
    if (edit && tools.length === 0) {
      client.get("/tools").then(({ data }) => setTools(data)).catch(() => {});
    }
  }, [edit?.customer_model]); // eslint-disable-line

  if (!edit) return null;

  const isVideoModel = (edit.media_type || "image") === "video";
  const applicableTools = tools.filter((t) => {
    const isVideoTool = ["text_to_video", "image_to_video", "video_extend"].includes(t.service);
    return isVideoModel ? isVideoTool : !isVideoTool;
  });
  const editServices = edit.services || [];
  const toggleService = (svc, on) => {
    const next = on ? [...new Set([...editServices, svc])] : editServices.filter((s) => s !== svc);
    setEdit({ ...edit, services: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (edit._new) {
        if (!edit.customer_model || !edit.label) {
          toast.error("Name and internal id are required"); setBusy(false); return;
        }
        await client.post("/admin/models", {
          customer_model: edit.customer_model, label: edit.label,
          tagline: edit.tagline || "", tier: edit.tier || "basic",
          media_type: edit.media_type || "image",
          provider_id: edit.provider_id || null,
          provider_model_id: edit.provider_model_id || "",
          est_cost: Number(edit.est_cost || 0),
          enabled: !!edit.enabled, services: edit.services || [],
        });
        toast.success("Model created");
      } else {
        await client.put("/admin/models", {
          customer_model: edit.customer_model,
          label: edit.label, tagline: edit.tagline, tier: edit.tier,
          media_type: edit.media_type,
          provider_id: edit.provider_id,
          provider_model_id: edit.provider_model_id,
          enabled: edit.enabled,
          est_cost: Number(edit.est_cost),
          prompt_enhancement_enabled: !!edit.prompt_enhancement_enabled,
          initial_prompt: edit.initial_prompt || "",
          services: edit.services || [],
          default_durations: edit.media_type === "video" ? (edit.default_durations || []) : null,
          default_resolutions: edit.media_type === "video" ? (edit.default_resolutions || []) : null,
        });
        toast.success("Saved");
      }
      setEdit(null);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    try {
      // Save first so preview reflects the just-typed template.
      await client.put("/admin/models", {
        customer_model: edit.customer_model,
        prompt_enhancement_enabled: !!edit.prompt_enhancement_enabled,
        initial_prompt: edit.initial_prompt || "",
      });
      const { data } = await client.post("/admin/models/prompt-preview", {
        customer_model: edit.customer_model,
        user_prompt: testPrompt,
      });
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="flex-shrink-0 border-b border-border px-6 py-4"><DialogTitle>{edit._new ? "Add New Model" : `Configure ${edit.label}`}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {edit._new && (
            <div>
              <Label>Internal ID (used in requests, lower-case, no spaces)</Label>
              <Input value={edit.customer_model} data-testid="new-model-id" placeholder="e.g. my-custom-model"
                     onChange={(e) => setEdit({ ...edit, customer_model: e.target.value.trim().toLowerCase().replace(/\s+/g, "-") })} className="mt-1.5 font-mono" />
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Name (shown on card)</Label>
              <Input value={edit.label || ""} data-testid="model-label"
                     onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="mt-1.5" placeholder="e.g. Google Veo 3.1 Fast" />
            </div>
            <div>
              <Label>Description / tagline</Label>
              <Input value={edit.tagline || ""} data-testid="model-tagline"
                     onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} className="mt-1.5" placeholder="Short marketing line" />
            </div>
            <div>
              <Label>Media type</Label>
              <select
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm"
                value={edit.media_type || "image"} data-testid="model-media-type"
                onChange={(e) => setEdit({ ...edit, media_type: e.target.value })}>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="text">Text</option>
                <option value="audio">Audio</option>
              </select>
            </div>
            <div>
              <Label>Tier</Label>
              <select
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm"
                value={edit.tier || "basic"} data-testid="model-tier"
                onChange={(e) => setEdit({ ...edit, tier: e.target.value })}>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Provider</Label>
              <select
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm"
                value={edit.provider_id || ""} data-testid="model-provider-select"
                onChange={(e) => setEdit({ ...edit, provider_id: e.target.value })}>
                <option value="">— None —</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Provider model ID</Label>
              <Input value={edit.provider_model_id || ""} data-testid="model-provider-model-id"
                     onChange={(e) => setEdit({ ...edit, provider_model_id: e.target.value })} className="mt-1.5 font-mono text-sm" />
            </div>
            <div>
              <Label>Est. cost ($)</Label>
              <Input type="number" step="0.001" value={edit.est_cost ?? 0} data-testid="model-est-cost"
                     onChange={(e) => setEdit({ ...edit, est_cost: e.target.value })} className="mt-1.5" />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex items-center gap-2">
                <Switch checked={!!edit.enabled} data-testid="model-enabled-switch"
                        onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} />
                <Label>Enabled / Live</Label>
              </div>
            </div>
          </div>

          {edit.media_type === "video" && (
            <div className="space-y-3">
              <div>
                <Label>Available video durations (admin-only)</Label>
                <p className="text-xs text-muted-foreground">Only the durations you check will appear in the customer's duration picker for this model.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[5, 10, 15, 20, 30].map((d) => {
                    const list = Array.isArray(edit.default_durations) && edit.default_durations.length
                      ? edit.default_durations
                      : (edit.default_duration ? [edit.default_duration] : []);
                    const on = list.includes(d);
                    return (
                      <label key={d} data-testid={`model-duration-${d}`}
                             className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${on ? "border-primary bg-primary/5" : "border-border"}`}>
                        <input type="checkbox" checked={on} className="h-4 w-4"
                               onChange={(e) => {
                                 const cur = new Set(list);
                                 if (e.target.checked) cur.add(d); else cur.delete(d);
                                 setEdit({ ...edit, default_durations: Array.from(cur).sort((a, b) => a - b) });
                               }} />
                        <span>{d}s</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Available video resolutions (admin-only)</Label>
                <p className="text-xs text-muted-foreground">Only the resolutions you check will appear in the customer's resolution picker for this model.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["480p", "720p", "1080p"].map((res) => {
                    const list = Array.isArray(edit.default_resolutions) ? edit.default_resolutions : [];
                    const on = list.includes(res);
                    return (
                      <label key={res} data-testid={`model-resolution-${res}`}
                             className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${on ? "border-primary bg-primary/5" : "border-border"}`}>
                        <input type="checkbox" checked={on} className="h-4 w-4"
                               onChange={(e) => {
                                 const cur = new Set(list);
                                 if (e.target.checked) cur.add(res); else cur.delete(res);
                                 setEdit({ ...edit, default_resolutions: Array.from(cur) });
                               }} />
                        <span>{res}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="border-t border-border pt-4">
            <p className="font-semibold">Available in tools</p>
            <p className="text-xs text-muted-foreground">Only checked tools will show this model in the customer dropdown.</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {applicableTools.map((t) => {
                const on = editServices.includes(t.service);
                return (
                  <label key={t.service} data-testid={`tool-avail-${t.service}`}
                         className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm ${on ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" checked={on} onChange={(e) => toggleService(t.service, e.target.checked)} className="h-4 w-4" />
                    <span>{t.name}</span>
                  </label>
                );
              })}
              {applicableTools.length === 0 && <p className="col-span-2 text-xs text-muted-foreground">No applicable tools loaded.</p>}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <p className="font-semibold">AI Model Prompt Settings</p>
            <p className="text-xs text-muted-foreground">
              When OFF, the user's prompt is sent verbatim to the model. When ON, the initial prompt below is applied.
              Use <code className="rounded bg-secondary px-1">{"{{prompt}}"}</code> as a placeholder for the user's prompt;
              if omitted, the user's prompt is appended after two newlines.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Switch checked={!!edit.prompt_enhancement_enabled} data-testid="prompt-enhancement-switch"
                      onCheckedChange={(v) => setEdit({ ...edit, prompt_enhancement_enabled: v })} />
              <Label>Prompt Enhancement</Label>
            </div>
            <div className="mt-3">
              <Label>Initial prompt / instruction (optional)</Label>
              <textarea
                data-testid="model-initial-prompt"
                value={edit.initial_prompt || ""}
                onChange={(e) => setEdit({ ...edit, initial_prompt: e.target.value })}
                rows={4}
                placeholder={`Example: Create a detailed professional anime artwork based on: {{prompt}}`}
                className="mt-1.5 w-full rounded-lg border border-border bg-background p-3 text-sm"
              />
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <p className="font-semibold">Test the final prompt</p>
            <div className="mt-3">
              <Label>Test user prompt</Label>
              <Input value={testPrompt} data-testid="prompt-test-input"
                     onChange={(e) => setTestPrompt(e.target.value)} className="mt-1.5" />
            </div>
            <Button variant="outline" size="sm" data-testid="prompt-preview-button" className="mt-3" onClick={runPreview}>
              Preview final prompt
            </Button>
            {preview && (
              <div className="mt-3 space-y-2 rounded-lg bg-secondary p-3 text-xs" data-testid="prompt-preview-output">
                <p><span className="font-semibold">Enhancement:</span> {preview.prompt_enhancement_enabled ? "ON" : "OFF"}</p>
                <p><span className="font-semibold">User prompt:</span> <span className="font-mono">{preview.user_prompt}</span></p>
                <p className="whitespace-pre-wrap"><span className="font-semibold">Final prompt sent to model:</span>
                  <br /><span className="font-mono">{preview.final_prompt}</span></p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t border-border px-6 py-4">
          <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
          <Button data-testid="model-save-button" disabled={busy} onClick={save}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToolsSection() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const load = () => {
    client.get("/admin/tools").then(({ data }) => setRows(data));
    client.get("/admin/models").then(({ data }) => setModels(data));
  };
  useEffect(() => { load(); }, []);
  const toggle = async (t, v) => { try { await client.put("/admin/tools", { service: t.service, enabled: v }); toast.success(`${t.name} ${v ? "enabled" : "disabled"}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleModel = async (tool, m, on) => {
    const svcs = m.services || [];
    const next = on ? [...new Set([...svcs, tool.service])] : svcs.filter((s) => s !== tool.service);
    try {
      await client.put("/admin/models", { customer_model: m.customer_model, services: next });
      toast.success(`${m.label} ${on ? "added to" : "removed from"} ${tool.name}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const applicableModels = (tool) => {
    const isVideo = ["text_to_video", "image_to_video", "video_extend"].includes(tool.service);
    return models.filter((m) => (m.media_type === "video") === isVideo);
  };
  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Tool Management</h1>
      <p className="mb-4 text-sm text-muted-foreground">Enable / disable a whole tool, and control which customer models show up per tool.</p>
      <div className="space-y-4">
        {rows.map((t) => (
          <div key={t.id} className="studio-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.category} · <span className="font-mono">{t.service}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${t.enabled ? "text-emerald-600" : "text-muted-foreground"}`}>{t.enabled ? "Live" : "Hidden"}</span>
                <Switch checked={t.enabled} data-testid={`tool-toggle-${t.service}`} onCheckedChange={(v) => toggle(t, v)} />
              </div>
            </div>
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-semibold text-muted-foreground">Models available in this tool</p>
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                {applicableModels(t).map((m) => {
                  const on = (m.services || []).includes(t.service);
                  return (
                    <div key={m.customer_model} data-testid={`tool-${t.service}-model-${m.customer_model}`}
                         className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2 text-sm">
                      <span className={m.enabled ? "" : "text-muted-foreground"}>{m.label}{!m.enabled && <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">(hidden)</span>}</span>
                      <Switch checked={on} onCheckedChange={(v) => toggleModel(t, m, v)} />
                    </div>
                  );
                })}
                {applicableModels(t).length === 0 && <p className="col-span-2 text-xs text-muted-foreground">No compatible models — create one from Customer Models.</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsSection() {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const save = async () => {
    const lv = Number(s.login_video_max_mb);
    const gv = Number(s.gallery_video_max_mb);
    if (!Number.isInteger(lv) || lv < 1 || lv > 500) return toast.error("Login Video max size must be an integer between 1 and 500 MB");
    if (!Number.isInteger(gv) || gv < 1 || gv > 500) return toast.error("Gallery video max size must be an integer between 1 and 500 MB");
    try { await client.put("/admin/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="max-w-xl">
      <h1 className="mb-6 font-display text-2xl font-extrabold">System Settings</h1>
      <div className="studio-card space-y-5 p-6">
        <div><Label>Brand name</Label><Input value={s.brand_name || ""} onChange={(e) => setS({ ...s, brand_name: e.target.value })} className="mt-1.5" data-testid="settings-brand" /></div>
        <div><Label>Support email</Label><Input value={s.support_email || ""} onChange={(e) => setS({ ...s, support_email: e.target.value })} className="mt-1.5" /></div>
        <div>
          <Label>Admin notification email</Label>
          <Input value={s.admin_notification_email || ""} data-testid="settings-admin-notification-email"
                 placeholder="e.g. admin@yourcompany.com"
                 onChange={(e) => setS({ ...s, admin_notification_email: e.target.value })} className="mt-1.5" />
          <p className="mt-1 text-xs text-muted-foreground">Sends an email here on every successful credit purchase. Leave empty to disable.</p>
        </div>
        <div><Label>Signup bonus credits</Label><Input type="number" value={s.signup_bonus_credits ?? 0} onChange={(e) => setS({ ...s, signup_bonus_credits: Number(e.target.value) })} className="mt-1.5" /></div>
        <div><Label>Max upload (MB)</Label><Input type="number" value={s.max_upload_mb ?? 10} onChange={(e) => setS({ ...s, max_upload_mb: Number(e.target.value) })} className="mt-1.5" /></div>

        <div className="border-t border-border pt-5">
          <p className="font-semibold">Video upload limits</p>
          <p className="text-xs text-muted-foreground">Individually configurable per feature. Must be between 1 and 500 MB.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Login Video max upload size (MB)</Label>
              <Input
                type="number" min={1} max={500}
                value={s.login_video_max_mb ?? 50}
                onChange={(e) => setS({ ...s, login_video_max_mb: Number(e.target.value) })}
                className="mt-1.5"
                data-testid="settings-login-video-max-mb"
              />
            </div>
            <div>
              <Label>Gallery video max upload size (MB)</Label>
              <Input
                type="number" min={1} max={500}
                value={s.gallery_video_max_mb ?? 25}
                onChange={(e) => setS({ ...s, gallery_video_max_mb: Number(e.target.value) })}
                className="mt-1.5"
                data-testid="settings-gallery-video-max-mb"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <p className="font-semibold">Payment Gateway</p>
          <p className="text-xs text-muted-foreground">Choose the active gateway for credit purchases. Both can be configured; only the selected one is offered at checkout.</p>
          <div className="mt-3">
            <Label>Active gateway</Label>
            <select
              data-testid="settings-payment-gateway"
              value={s.payment_gateway || "razorpay"}
              onChange={(e) => setS({ ...s, payment_gateway: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm"
            >
              <option value="razorpay">Razorpay</option>
              <option value="stripe">Stripe</option>
            </select>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <p className="font-semibold">Razorpay Payment Gateway</p>
          <p className="text-xs text-muted-foreground">Configure your live/test keys. Keys set here override environment values. The secret is never sent back to the browser.</p>
          <div className="mt-3 space-y-3">
            <div><Label>Key ID</Label><Input value={s.razorpay_key_id || ""} placeholder="rzp_live_xxx or rzp_test_xxx" data-testid="settings-razorpay-key-id" onChange={(e) => setS({ ...s, razorpay_key_id: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Key Secret {s.has_razorpay_secret && <span className="text-xs text-emerald-600">(configured)</span>}</Label><Input type="password" value={s.razorpay_key_secret || ""} placeholder="Enter to update" data-testid="settings-razorpay-key-secret" onChange={(e) => setS({ ...s, razorpay_key_secret: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Webhook Secret {s.has_razorpay_webhook_secret && <span className="text-xs text-emerald-600">(configured)</span>}</Label><Input type="password" value={s.razorpay_webhook_secret || ""} placeholder="Enter to update" data-testid="settings-razorpay-webhook-secret" onChange={(e) => setS({ ...s, razorpay_webhook_secret: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
          </div>
        </div>

        <div className="border-t border-border pt-5">
          <p className="font-semibold">Stripe Payment Gateway</p>
          <p className="text-xs text-muted-foreground">Paste your Stripe API keys. Charges are made in USD; the customer-facing price is auto-converted from INR based on FX.</p>
          <div className="mt-3 space-y-3">
            <div><Label>Publishable key</Label><Input value={s.stripe_publishable_key || ""} placeholder="pk_test_… or pk_live_…" data-testid="settings-stripe-publishable" onChange={(e) => setS({ ...s, stripe_publishable_key: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Secret key {s.has_stripe_secret && <span className="text-xs text-emerald-600">(configured)</span>}</Label><Input type="password" value={s.stripe_secret_key || ""} placeholder="sk_test_… or sk_live_…" data-testid="settings-stripe-secret" onChange={(e) => setS({ ...s, stripe_secret_key: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
            <div><Label>Webhook secret {s.has_stripe_webhook_secret && <span className="text-xs text-emerald-600">(configured)</span>}</Label><Input type="password" value={s.stripe_webhook_secret || ""} placeholder="whsec_…" data-testid="settings-stripe-webhook" onChange={(e) => setS({ ...s, stripe_webhook_secret: e.target.value })} className="mt-1.5 font-mono text-sm" /></div>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div><p className="font-medium">Maintenance mode</p><p className="text-xs text-muted-foreground">Show a friendly message to users</p></div>
          <Switch checked={s.maintenance_mode} data-testid="settings-maintenance" onCheckedChange={(v) => setS({ ...s, maintenance_mode: v })} />
        </div>
        <Button onClick={save} className="rounded-full" data-testid="settings-save">Save settings</Button>
      </div>
    </div>
  );
}

function EditDialog({ open, onClose, title, fields, value, setValue, onSave }) {
  if (!fields) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              {f.type === "switch" ? (
                <div className="mt-1.5"><Switch checked={!!value[f.key]} onCheckedChange={(v) => setValue({ ...value, [f.key]: v })} /></div>
              ) : f.type === "textarea" ? (
                <textarea rows={4} className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm" value={value[f.key] || ""} onChange={(e) => setValue({ ...value, [f.key]: e.target.value })} />
              ) : f.type === "select" ? (
                <select className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm" value={value[f.key] || ""} onChange={(e) => setValue({ ...value, [f.key]: e.target.value })}>
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <Input type={f.type || "text"} className="mt-1.5" value={value[f.key] ?? ""} onChange={(e) => setValue({ ...value, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={onSave} data-testid="dialog-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function GenerationsSection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    const { data } = await client.get(`/admin/generations?page=${page}&limit=${limit}`);
    setItems(data.items || []); setTotal(data.total || 0);
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.ceil(total / limit);

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Generations</h1>
      <p className="mb-4 text-sm text-muted-foreground">Every request routed to the AI providers — including the exact prompt sent. Click any row for full details.</p>
      <div className="studio-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Tool</th>
                <th className="p-3 text-left">Model</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">User prompt</th>
                <th className="p-3 text-left">Final prompt</th>
                <th className="p-3 text-left">Req / Resp</th>
                <th className="p-3 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} data-testid={`gen-row-${g.id}`} className="cursor-pointer border-t border-border hover:bg-secondary/50" onClick={() => setSelected(g)}>
                  <td className="p-3 font-mono text-xs">{g.id.slice(0, 12)}</td>
                  <td className="p-3">{g.service}</td>
                  <td className="p-3 capitalize">{g.model}</td>
                  <td className="p-3"><StatusBadge s={g.status} /></td>
                  <td className="p-3 max-w-[220px] truncate" title={g.prompt}>{g.prompt || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 max-w-[260px] truncate" title={g.final_prompt}>
                    {g.final_prompt || <span className="text-muted-foreground">{g.prompt || "—"}</span>}
                  </td>
                  <td className="p-3 max-w-[260px] align-top" data-testid={`gen-req-resp-${g.id}`}>
                    {(g.provider_request || g.provider_response) ? (
                      <div className="space-y-1">
                        <div className="truncate font-mono text-[11px] text-muted-foreground" title={g.provider_request ? JSON.stringify(g.provider_request) : ""}>
                          <span className="text-foreground">req:</span> {g.provider_request ? JSON.stringify(g.provider_request).slice(0, 80) : "—"}
                        </div>
                        <div className="truncate font-mono text-[11px] text-muted-foreground" title={g.provider_response ? JSON.stringify(g.provider_response) : ""}>
                          <span className="text-foreground">resp:</span> {g.provider_response ? JSON.stringify(g.provider_response).slice(0, 80) : "—"}
                        </div>
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(g.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No generations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {pages > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="grid place-items-center px-3 text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Generation {selected?.id?.slice(0, 16)}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Tool</span><p className="font-semibold">{selected.service}</p></div>
                <div><span className="text-muted-foreground">Model</span><p className="font-semibold capitalize">{selected.model}</p></div>
                <div><span className="text-muted-foreground">Provider used</span><p className="font-semibold">{selected.provider_used || "—"}</p></div>
                <div><span className="text-muted-foreground">Status</span><StatusBadge s={selected.status} /></div>
              </div>
              <div>
                <span className="text-muted-foreground">User prompt (what the customer typed)</span>
                <p className="mt-1 whitespace-pre-wrap rounded-lg bg-secondary p-3 font-mono text-xs" data-testid="gen-detail-user-prompt">{selected.prompt || "—"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Final prompt (what was sent to the AI provider)</span>
                <p className="mt-1 whitespace-pre-wrap rounded-lg bg-secondary p-3 font-mono text-xs" data-testid="gen-detail-final-prompt">{selected.final_prompt || selected.prompt || "—"}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  To adjust how prompts are transformed, edit this model's Initial Prompt in <b>Customer Models → Configure</b>.
                </p>
              </div>
              {selected.error_message && (
                <div>
                  <span className="text-muted-foreground">Error</span>
                  <p className="mt-1 whitespace-pre-wrap rounded-lg bg-destructive/10 p-3 font-mono text-xs text-destructive">{selected.error_code ? `[${selected.error_code}] ` : ""}{selected.error_message}</p>
                </div>
              )}
              {(selected.provider_request || selected.provider_response) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Provider request</span>
                    <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-secondary p-3 font-mono text-[11px]" data-testid="gen-detail-provider-request">{selected.provider_request ? JSON.stringify(selected.provider_request, null, 2) : "—"}</pre>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Provider response</span>
                    <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-secondary p-3 font-mono text-[11px]" data-testid="gen-detail-provider-response">{selected.provider_response ? JSON.stringify(selected.provider_response, null, 2) : "—"}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminsSection() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null); // { email, temp_password }

  const load = useCallback(async () => {
    const { data } = await client.get("/admin/users?limit=200");
    setItems((data.items || []).filter((u) => u.role === "admin"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email || form.password.length < 8) {
      toast.error("Enter email and a password of 8+ characters");
      return;
    }
    setBusy(true);
    try {
      await client.post("/admin/users/create-admin", form);
      toast.success("Admin created — they'll enroll MFA on first login");
      setShowCreate(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const reset = async (uid) => {
    if (!window.confirm("Generate a new temporary password for this admin? Their active sessions will be revoked.")) return;
    try {
      const { data } = await client.post(`/admin/users/${uid}/reset-password`, {});
      setTempPw({ email: data.email, temp_password: data.temp_password });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const disableMfa = async (uid) => {
    if (!window.confirm("Reset this admin's MFA? They'll be forced to re-enroll on next login.")) return;
    try {
      await client.post(`/admin/users/${uid}/disable-mfa`, {});
      toast.success("MFA reset");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Admins</h2>
          <p className="text-sm text-muted-foreground">Create new administrators, reset passwords and MFA.</p>
        </div>
        <Button data-testid="create-admin-button" onClick={() => setShowCreate(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Create admin</Button>
      </div>

      <div className="studio-card divide-y">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">No admins yet.</p>}
        {items.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <p className="font-semibold">{u.name || u.email}</p>
              <p className="text-xs text-muted-foreground">{u.email} · {u.mfa_enabled ? <span className="text-emerald-600">MFA enabled</span> : <span className="text-amber-600">MFA pending</span>}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-testid={`reset-pw-${u.user_id}`} onClick={() => reset(u.user_id)}>Reset password</Button>
              <Button variant="outline" size="sm" data-testid={`reset-mfa-${u.user_id}`} onClick={() => disableMfa(u.user_id)}>Reset MFA</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create admin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@company.com" className="mt-1.5" /></div>
            <div><Label>Temporary password</Label><Input data-testid="new-admin-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" className="mt-1.5" /></div>
            <p className="text-xs text-muted-foreground">The new admin will be forced to enroll MFA on first login.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="create-admin-submit" disabled={busy} onClick={create}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPw} onOpenChange={(o) => { if (!o) setTempPw(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          {tempPw && (
            <div className="space-y-3">
              <p className="text-sm">Share this <b>one-time</b> password with <b>{tempPw.email}</b> via a secure channel. Their sessions were revoked.</p>
              <div className="rounded-lg bg-secondary p-3 font-mono text-sm break-all" data-testid="temp-password">{tempPw.temp_password}</div>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(tempPw.temp_password); toast.success("Copied"); }}>Copy password</Button>
            </div>
          )}
          <DialogFooter><Button onClick={() => setTempPw(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================================================
   Admin-managed content: Home tiles / Gallery / Login video
   All three sections share the same upload endpoint at
   POST /api/admin/public-uploads?kind=image|video and rely on
   the public GET /api/public-media/{path} for serving.
   ============================================================ */

function useAdminUploader(kind = "image") {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/public-uploads?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } finally {
      setBusy(false);
    }
  };
  return { upload, busy };
}

function HomeTilesSection() {
  const [tiles, setTiles] = useState([]);
  const [hero, setHero] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newName, setNewName] = useState("");
  const loadAll = () => {
    client.get("/home-tiles").then(({ data }) => setTiles(data));
    client.get("/hero-cards").then(({ data }) => setHero(data));
  };
  useEffect(() => { loadAll(); }, []);

  const saveTile = async (key, patch) => {
    try { await client.put(`/admin/home-tiles/${key}`, patch); toast.success("Saved"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteTile = async (key) => {
    if (!window.confirm(`Delete tile "${key}"? This cannot be undone.`)) return;
    try { await client.delete(`/admin/home-tiles/${key}`); toast.success("Tile deleted"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createTile = async () => {
    try {
      await client.post("/admin/home-tiles", { key: newKey.trim().toLowerCase().replace(/\s+/g, "-"), display_name: newName.trim() });
      setCreating(false); setNewKey(""); setNewName(""); toast.success("Tile created"); loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveHero = async (key, patch) => {
    try { await client.put(`/admin/hero-cards/${key}`, patch); toast.success("Saved"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-10">
      <div>
        <h1 className="mb-3 font-display text-2xl font-extrabold">Home Tiles</h1>
        <p className="mb-6 text-sm text-muted-foreground">Configure the landing page hero collage and the "Made with AdGen Store" tiles.</p>
      </div>

      <div>
        <h2 className="mb-4 text-lg font-bold">Hero collage cards</h2>
        <p className="mb-4 text-sm text-muted-foreground">Four fixed slots on the landing hero. Link each card to a "Made with AdGen Store" tile below — its image pool will drive that hero card's slideshow. Manual image overrides only apply when a card isn't linked.</p>
        <div className="grid gap-4 md:grid-cols-2">
          {hero.map((c) => <HeroCardEditor key={c.key} card={c} tiles={tiles} onSave={(p) => saveHero(c.key, p)} />)}
        </div>
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Made with AdGen Store tiles</h2>
            <p className="text-sm text-muted-foreground">The category-list line beside the section heading is generated from the tile names below.</p>
          </div>
          <Button className="rounded-full" data-testid="add-tile-button" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add tile
          </Button>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {tiles.map((t) => (
            <TileCard key={t.key} tile={t} onSave={(p) => saveTile(t.key, p)} onDelete={() => deleteTile(t.key)} />
          ))}
        </div>
      </div>

      <Dialog open={creating} onOpenChange={(o) => { if (!o) setCreating(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add a new tile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Display name</Label>
              <Input className="mt-1.5" value={newName} data-testid="new-tile-name" onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Technology" />
            </div>
            <div>
              <Label>Key <span className="text-muted-foreground text-xs">(lowercase, no spaces — permanent internal ID)</span></Label>
              <Input className="mt-1.5 font-mono" value={newKey} data-testid="new-tile-key" onChange={(e) => setNewKey(e.target.value)} placeholder="e.g. technology" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button data-testid="new-tile-create" disabled={!newKey.trim() || !newName.trim()} onClick={createTile}>Create tile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HeroCardEditor({ card, tiles = [], onSave }) {
  const [label, setLabel] = useState(card.label || "");
  const { upload, busy } = useAdminUploader("image");
  const inputRef = React.useRef();
  const linked = tiles.find((t) => t.key === card.linked_tile_key);
  const previewUrls = linked?.images?.length ? linked.images : (card.image_url ? [card.image_url] : []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const url = await upload(f);
      onSave({ image_url: url });
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="studio-card p-5" data-testid={`hero-card-${card.key}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="pill-cyan">{card.key}</span>
        {linked
          ? <span className="text-xs text-primary">linked → {linked.display_name}</span>
          : card.image_url
            ? <span className="text-xs text-emerald-500">manual override</span>
            : <span className="text-xs text-muted-foreground">using fallback</span>}
      </div>

      <div className="mb-4 overflow-hidden rounded-lg border border-border bg-black/40">
        {previewUrls.length
          ? <img src={previewUrls[0]} alt="" className="aspect-video w-full object-cover" />
          : <div className="grid aspect-video w-full place-items-center text-xs text-muted-foreground">Using default gallery fallback</div>}
      </div>

      <div className="mb-4">
        <Label>Linked "Made with AdGen Store" tile</Label>
        <select
          className="mt-1.5 w-full rounded-lg border border-border bg-background p-2 text-sm"
          value={card.linked_tile_key || ""}
          data-testid={`hero-link-${card.key}`}
          onChange={(e) => {
            const v = e.target.value;
            onSave(v ? { linked_tile_key: v } : { clear_link: true });
          }}
        >
          <option value="">— No link (use manual override / fallback) —</option>
          {tiles.map((t) => (
            <option key={t.key} value={t.key}>{t.display_name} ({(t.images || []).length} images)</option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-muted-foreground">When linked, this card's slideshow uses the tile's image pool live — including any images the tile is later given.</p>
      </div>

      <div className="mb-3">
        <Label>Label pill (leave empty to hide)</Label>
        <div className="mt-1.5 flex gap-2">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`hero-label-${card.key}`} placeholder="e.g. Product Ad" />
          <Button variant="outline" onClick={() => onSave(label ? { label } : { clear_label: true })}>Save</Button>
        </div>
      </div>

      {!card.linked_tile_key && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`hero-upload-${card.key}`}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} {card.image_url ? "Replace manual image" : "Upload manual image"}
          </Button>
          {card.image_url && (
            <Button size="sm" variant="outline" onClick={() => onSave({ image_url: "" })} data-testid={`hero-clear-${card.key}`}>
              <Trash2 className="mr-1 h-4 w-4" /> Revert to default
            </Button>
          )}
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} />
        </div>
      )}
    </div>
  );
}

function TileCard({ tile, onSave, onDelete }) {
  const [name, setName] = useState(tile.display_name || "");
  const [imgs, setImgs] = useState(tile.images || []);
  const { upload, busy } = useAdminUploader("image");
  const inputRef = React.useRef();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      try {
        const url = await upload(f);
        setImgs((prev) => {
          const next = [...prev, url];
          onSave({ display_name: name, images: next });
          return next;
        });
      } catch (err) { toast.error(apiErr(err)); }
    }
  };
  const removeAt = (i) => {
    setImgs((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      onSave({ display_name: name, images: next });
      return next;
    });
  };
  const commitName = () => {
    if (name !== tile.display_name) onSave({ display_name: name });
  };
  return (
    <div className="studio-card p-5" data-testid={`tile-${tile.key}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="pill-cyan">{tile.key}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{imgs.length} images</span>
          {onDelete && (
            <button className="rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    data-testid={`tile-delete-${tile.key}`} onClick={onDelete} title="Delete tile">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <Label>Display name</Label>
      <Input className="mt-1.5 mb-4" value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName} data-testid={`tile-name-${tile.key}`} />
      <div className="grid grid-cols-3 gap-2">
        {imgs.map((url, i) => (
          <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => removeAt(i)} data-testid={`tile-remove-${tile.key}-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button onClick={() => inputRef.current?.click()} disabled={busy || imgs.length >= 8}
                className="grid aspect-square place-items-center rounded-lg border border-dashed border-primary/40 text-xs text-primary hover:bg-primary/5 disabled:opacity-40"
                data-testid={`tile-upload-${tile.key}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /><span className="ml-1">Add</span></>}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFile} />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">Up to 8 images. Renaming the tile does not break existing images (they’re associated by the <code>{tile.key}</code> key).</p>
    </div>
  );
}

function GalleryManagerSection() {
  const [items, setItems] = useState([]);
  const [maxMb, setMaxMb] = useState(25);
  const { upload: uploadImg, busy: busyImg } = useAdminUploader("image");
  const { upload: uploadVid, busy: busyVid } = useAdminUploader("video");
  const imgInput = React.useRef();
  const vidInput = React.useRef();
  const load = () => client.get("/gallery-items").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    client.get("/admin/settings").then(({ data }) => setMaxMb(Number(data?.gallery_video_max_mb) || 25)).catch(() => {});
  }, []);

  const addFiles = async (files, kind) => {
    for (const f of files) {
      try {
        const url = kind === "video" ? await uploadVid(f) : await uploadImg(f);
        await client.post("/admin/gallery-items", { type: kind === "video" ? "video" : "photo", url });
      } catch (e) { toast.error(apiErr(e)); }
    }
    load();
  };
  const removeItem = async (id) => { try { await client.delete(`/admin/gallery-items/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const move = async (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setItems(next);
    try { await client.put("/admin/gallery-items/reorder", { order: next.map((i) => i.id) }); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const photos = items.filter((i) => i.type === "photo");
  const videos = items.filter((i) => i.type === "video");

  const grid = (list, kind) => (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {list.map((it) => {
        const idx = items.indexOf(it);
        return (
          <div key={it.id} className="group relative overflow-hidden rounded-lg border border-border bg-secondary/30" data-testid={`gallery-item-${it.id}`}>
            {kind === "video"
              ? <video src={it.url} muted playsInline preload="metadata" className="aspect-square w-full object-cover" />
              : <img src={it.url} alt="" className="aspect-square w-full object-cover" />}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/70 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
              <div className="flex gap-1">
                <button className="grid h-6 w-6 place-items-center rounded bg-white/10 text-white" onClick={() => move(idx, idx - 1)} title="Move earlier">↑</button>
                <button className="grid h-6 w-6 place-items-center rounded bg-white/10 text-white" onClick={() => move(idx, idx + 1)} title="Move later">↓</button>
              </div>
              <button className="grid h-6 w-6 place-items-center rounded bg-destructive/80 text-white" onClick={() => removeItem(it.id)}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        );
      })}
      {list.length === 0 && <p className="col-span-full p-6 text-center text-sm text-muted-foreground">No {kind}s uploaded yet.</p>}
    </div>
  );

  return (
    <div className="space-y-8">
      <h1 className="font-display text-2xl font-extrabold">Gallery Manager</h1>
      <div className="studio-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">Gallery photos</h2>
          <Button size="sm" onClick={() => imgInput.current?.click()} disabled={busyImg} data-testid="gallery-upload-photos">
            {busyImg ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Upload photos
          </Button>
          <input ref={imgInput} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(Array.from(e.target.files || []), "photo"); e.target.value = ""; }} />
        </div>
        {grid(photos, "photo")}
      </div>
      <div className="studio-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Gallery videos</h2>
            <p className="text-xs text-muted-foreground">Upload mp4 / webm / mov (max {maxMb} MB each).</p>
          </div>
          <Button size="sm" onClick={() => vidInput.current?.click()} disabled={busyVid} data-testid="gallery-upload-videos">
            {busyVid ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Upload videos
          </Button>
          <input ref={vidInput} type="file" accept="video/mp4,video/webm,video/quicktime" multiple hidden onChange={(e) => { addFiles(Array.from(e.target.files || []), "video"); e.target.value = ""; }} />
        </div>
        {grid(videos, "video")}
      </div>
    </div>
  );
}

function LoginVideoSection() {
  const [playlist, setPlaylist] = useState([]);
  const [readyIdx, setReadyIdx] = useState(0);
  const [maxMb, setMaxMb] = useState(50);
  const { upload, busy } = useAdminUploader("login_video");
  const inputRef = React.useRef();

  const load = () => client.get("/login-videos").then(({ data }) => setPlaylist(Array.isArray(data) ? data : []));
  useEffect(() => {
    load();
    client.get("/admin/settings").then(({ data }) => setMaxMb(Number(data?.login_video_max_mb) || 50)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      try {
        const newUrl = await upload(f);
        await client.post("/admin/login-videos", { url: newUrl, filename: f.name, size: f.size });
      } catch (err) { toast.error(apiErr(err)); }
    }
    await load();
    toast.success("Video(s) added");
  };
  const remove = async (id) => {
    try { await client.delete(`/admin/login-videos/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const move = async (from, to) => {
    if (to < 0 || to >= playlist.length || from === to) return;
    const next = [...playlist];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    setPlaylist(next);
    try { await client.put("/admin/login-videos/reorder", { order: next.map((v) => v.id) }); toast.success("Order saved"); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const sizeMb = (b) => b ? `${(b / (1024 * 1024)).toFixed(1)} MB` : "—";

  return (
    <div>
      <h1 className="mb-3 font-display text-2xl font-extrabold">Login video background</h1>
      <p className="mb-6 text-sm text-muted-foreground">Upload one or more short mp4 clips (max {maxMb} MB each). The login page plays them in this order, then loops back to the first. Muted &amp; autoplay.</p>

      <div className="studio-card mb-6 overflow-hidden">
        <div className="aspect-video w-full bg-black/40" data-testid="admin-login-video-preview-wrap">
          {playlist.length > 0 ? (
            <video
              key={playlist[readyIdx]?.id}
              src={playlist[readyIdx]?.url}
              className="h-full w-full object-cover"
              autoPlay muted playsInline preload="auto"
              loop={playlist.length === 1}
              onEnded={() => setReadyIdx((i) => (i + 1) % playlist.length)}
              data-testid="admin-login-video-preview"
            />
          ) : (
            <div className="grid h-full place-items-center text-sm text-muted-foreground">No videos uploaded — the login page will show the default static image.</div>
          )}
        </div>
        <div className="flex items-center gap-3 p-5">
          <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="login-video-upload-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Upload video
          </Button>
          <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime" multiple hidden onChange={onFile} />
          <span className="text-xs text-muted-foreground">{playlist.length} in playlist</span>
        </div>
      </div>

      <div className="space-y-3">
        {playlist.map((v, i) => (
          <div key={v.id} className="studio-card flex items-center gap-4 p-3" data-testid={`login-video-item-${v.id}`}>
            <video src={v.url} muted playsInline preload="metadata" className="h-16 w-24 rounded-md object-cover bg-black" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{v.filename || "video.mp4"}</p>
              <p className="text-xs text-muted-foreground">#{i + 1} · {sizeMb(v.size)}</p>
            </div>
            <div className="flex items-center gap-1">
              <button className="grid h-8 w-8 place-items-center rounded-md border border-border hover:bg-secondary" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`login-video-up-${v.id}`}>↑</button>
              <button className="grid h-8 w-8 place-items-center rounded-md border border-border hover:bg-secondary" onClick={() => move(i, i + 1)} disabled={i === playlist.length - 1} data-testid={`login-video-down-${v.id}`}>↓</button>
              <button className="grid h-8 w-8 place-items-center rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => remove(v.id)} data-testid={`login-video-remove-${v.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {playlist.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No videos yet. Upload one to begin the playlist.</p>}
      </div>
    </div>
  );
}



// ------------------- Legal Pages editor -------------------
function LegalPagesSection() {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState("terms");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingSlug, setLoadingSlug] = useState(true);

  const loadList = async () => {
    const { data } = await client.get("/admin/legal-pages");
    setPages(data);
  };
  useEffect(() => { loadList(); }, []);

  // Whenever the selected slug changes, load its current draft (saved override
  // if any). If nothing is saved yet, seed the editor with an empty starter
  // shell so the admin can build the page from scratch and hit Save.
  useEffect(() => {
    let alive = true;
    setLoadingSlug(true);
    (async () => {
      try {
        const { data } = await client.get(`/legal-pages/${slug}`);
        if (!alive) return;
        if (data && data.override) {
          setDraft({ title: data.title, last_updated: data.last_updated, sections: data.sections });
        } else {
          setDraft({
            title: pages.find((p) => p.slug === slug)?.label || "",
            last_updated: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "2-digit" }),
            sections: [{ heading: "", body: [""] }],
          });
        }
      } finally { if (alive) setLoadingSlug(false); }
    })();
    return () => { alive = false; };
  }, [slug, pages]);

  const updateSection = (i, patch) => {
    setDraft((d) => ({ ...d, sections: d.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  };
  const updateParagraph = (i, j, value) => {
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, idx) => idx === i ? { ...s, body: s.body.map((p, pj) => pj === j ? value : p) } : s),
    }));
  };
  const addParagraph = (i) => updateSection(i, { body: [...draft.sections[i].body, ""] });
  const removeParagraph = (i, j) => updateSection(i, { body: draft.sections[i].body.filter((_, pj) => pj !== j) });
  const addSection = () => setDraft((d) => ({ ...d, sections: [...d.sections, { heading: "", body: [""] }] }));
  const removeSection = (i) => setDraft((d) => ({ ...d, sections: d.sections.filter((_, idx) => idx !== i) }));
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= draft.sections.length) return;
    const next = [...draft.sections];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, sections: next });
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim()) return toast.error("Title cannot be empty");
    if (!draft.last_updated.trim()) return toast.error('"Last updated" cannot be empty');
    if (!draft.sections.length) return toast.error("Add at least one section");
    for (let i = 0; i < draft.sections.length; i++) {
      const s = draft.sections[i];
      if (!s.heading.trim()) return toast.error(`Section ${i + 1} needs a heading`);
      if (!s.body.some((p) => p.trim())) return toast.error(`Section ${i + 1} needs at least one paragraph`);
    }
    setSaving(true);
    try {
      await client.put(`/admin/legal-pages/${slug}`, draft);
      toast.success("Saved. Public page will refresh on next load.");
      await loadList();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const revert = async () => {
    if (!window.confirm("Revert this page to the built-in default? Your saved edits will be lost.")) return;
    try {
      await client.delete(`/admin/legal-pages/${slug}`);
      toast.success("Reverted to default");
      await loadList();
      const { data } = await client.get(`/legal-pages/${slug}`);
      if (data && data.override) {
        setDraft({ title: data.title, last_updated: data.last_updated, sections: data.sections });
      } else {
        setDraft({
          title: pages.find((p) => p.slug === slug)?.label || "",
          last_updated: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "2-digit" }),
          sections: [{ heading: "", body: [""] }],
        });
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const current = pages.find((p) => p.slug === slug);

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-extrabold">Legal Pages</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Edit the public /terms, /privacy and other policy pages. When you save an override, the public page loads it live.
        If you never save, the built-in default that ships with the app is shown.
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Label htmlFor="legal-slug" className="text-sm">Editing:</Label>
        <select
          id="legal-slug"
          data-testid="legal-page-select"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          {pages.map((p) => (
            <option key={p.slug} value={p.slug}>{p.label}{p.has_override ? "  (edited)" : ""}</option>
          ))}
        </select>
        {current?.has_override && (
          <Button size="sm" variant="outline" onClick={revert} data-testid="legal-page-revert">Revert to default</Button>
        )}
        <a href={`/${slug}`} target="_blank" rel="noreferrer" className="text-xs text-primary underline-offset-4 hover:underline">Open public page ↗</a>
      </div>

      {loadingSlug || !draft ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : (
        <div className="studio-card space-y-6 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Page title</Label>
              <Input
                data-testid="legal-page-title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Last updated</Label>
              <Input
                data-testid="legal-page-last-updated"
                value={draft.last_updated}
                onChange={(e) => setDraft({ ...draft, last_updated: e.target.value })}
                className="mt-1.5"
                placeholder="e.g. September 08, 2026"
              />
            </div>
          </div>

          <div className="space-y-4">
            {draft.sections.map((s, i) => (
              <div key={i} className="rounded-lg border border-border p-4" data-testid={`legal-page-section-${i}`}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">#{i + 1}</span>
                  <Input
                    value={s.heading}
                    onChange={(e) => updateSection(i, { heading: e.target.value })}
                    placeholder="Section heading"
                    data-testid={`legal-page-section-heading-${i}`}
                    className="flex-1"
                  />
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up">↑</Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => moveSection(i, +1)} disabled={i === draft.sections.length - 1} title="Move down">↓</Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => removeSection(i)} title="Remove section" data-testid={`legal-page-section-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {s.body.map((para, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <textarea
                        value={para}
                        onChange={(e) => updateParagraph(i, j, e.target.value)}
                        placeholder={`Paragraph ${j + 1}`}
                        data-testid={`legal-page-section-${i}-paragraph-${j}`}
                        className="min-h-[80px] flex-1 rounded-md border border-border bg-background p-2 text-sm leading-relaxed"
                      />
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => removeParagraph(i, j)} disabled={s.body.length === 1} title="Remove paragraph">
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => addParagraph(i)} data-testid={`legal-page-section-add-paragraph-${i}`}>
                    <Plus className="mr-1 h-4 w-4" /> Add paragraph
                  </Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addSection} data-testid="legal-page-add-section">
              <Plus className="mr-1 h-4 w-4" /> Add section
            </Button>
          </div>

          <div className="flex items-center gap-3 border-t border-border pt-4">
            <Button onClick={save} disabled={saving} data-testid="legal-page-save">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />} Save
            </Button>
            <p className="text-xs text-muted-foreground">Changes appear on the public page immediately after save.</p>
          </div>
        </div>
      )}
    </div>
  );
}
